import { Fragment, useEffect, useRef, useState } from 'react';
import { Flex, Typography } from 'antd';
import { unByKey } from 'ol/Observable';
import { toLonLat } from 'ol/proj';

import { useProject } from '@contexts/Project.context';
import { useToast } from '@contexts/Toast.context';
import { useCollectionStore } from '@stores/useCollectionStore';
import { createTooltip } from '@utils/map/tooltip.overlay';
import { formatLength, formatArea } from '@utils/measurementSystem';
import { formatNumber } from '@utils/helpers';
import { objectLabel } from '../utils/transformUtils';
import { DETECTION_POINTER_LAYER_ID } from '../constants';

const { Text } = Typography;

const TOOLTIP_ID = 'collection-tooltip';

const COLLECTION_LAYER_ID_SET = new Set(Object.values(DETECTION_POINTER_LAYER_ID));

const isCollectionLayer = layer => COLLECTION_LAYER_ID_SET.has(layer?.get('customLayerId'));

/**
 * Retrieves the first collection detection feature at the given pixel.
 * Collection features are identified by having an `node_uuid` property
 * (set during buildShapeFeature from the GeoJSON response).
 * Returns null if nothing relevant is found.
 *
 * Restricted to the collection's own layers: this runs on every pointermove,
 * and hit-testing every layer on the map to then discard all but one kind of
 * feature is work spent on results that are thrown away.
 */
const getCollectionFeature = (mapObject, pixel) => {
	try {
		const features = mapObject.getFeaturesAtPixel(pixel, { layerFilter: isCollectionLayer });
		if (!features?.length) return null;
		return features.find(f => f.get('node_uuid')) ?? null;
	} catch {
		return null;
	}
};

/** Format a property value into a display string (or array of strings), or null if not applicable. */
const formatProperty = (key, value) => {
	if (value == null) return null;

	switch (key) {
		case 'confidence':
			return `Confidence: ${formatNumber(value * 100)}%`;
		case 'area': {
			const [num, unit] = formatArea(value);
			return num ? `Area: ${num} ${unit}` : null;
		}
		case 'radius': {
			const [num, unit] = formatLength(value * 2);
			return num ? `Diameter: ${num} ${unit}` : null;
		}
		case 'height': {
			const [num, unit] = formatLength(value);
			return num ? `Height: ${num} ${unit}` : null;
		}
		case 'estimated_stems': {
			const { low, medium, high } = value;
			if (low == null && medium == null && high == null) return null;
			return [
				`Est. Stems (Low): ${low != null ? formatNumber(low, 0) : '-'}`,
				`Est. Stems (Med): ${medium != null ? formatNumber(medium, 0) : '-'}`,
				`Est. Stems (High): ${high != null ? formatNumber(high, 0) : '-'}`,
			];
		}
		default:
			return null;
	}
};

/**
 * Provides hover-tooltip and click-to-copy-coords interactions for collection
 * detection features on the map.
 *
 * Mirrors the model workflow's Details layer behavior:
 * - Hover: shows object name + formatted measurements (respecting measurement system)
 * - Click: copies coordinates to clipboard
 *
 * Uses the shared `createTooltip` overlay + `.ol-tooltip` styling, and renders
 * tooltip content through React state. The caller provides a stable ref to a
 * DOM element (created imperatively, outside React's tree) that OpenLayers will
 * relocate into its overlay container. This avoids React "removeChild" errors
 * on unmount.
 *
 * Uses refs for values read inside event handlers that change independently of
 * the effect lifecycle (toolBarVisible, interactionsDisabled, addToast) to
 * avoid tearing down and re-registering OL listeners whenever those values
 * change identity (e.g. addToast is a new function on every toast shown
 * anywhere in the app).
 *
 * @param {boolean} enabled - Gate to disable interactions (e.g. no detections)
 * @param {React.RefObject<HTMLElement>} overlayElRef - Ref to the imperatively-created overlay element
 * @returns {{ tooltipContent: React.ReactNode }}
 */
const useCollectionTooltip = (enabled, overlayElRef) => {
	const { mapObject, toolBarVisible, defaultProjection, interactionsDisabled } = useProject();
	const { addToast } = useToast();

	const toolBarVisibleRef = useRef(toolBarVisible);
	const interactionsDisabledRef = useRef(interactionsDisabled);
	const projectionRef = useRef(defaultProjection);
	const addToastRef = useRef(addToast);

	useEffect(() => {
		toolBarVisibleRef.current = toolBarVisible;
		interactionsDisabledRef.current = interactionsDisabled;
		projectionRef.current = defaultProjection;
		addToastRef.current = addToast;
	});

	const [tooltipContent, setTooltipContent] = useState(null);

	useEffect(() => {
		if (!mapObject || !enabled || !overlayElRef.current) return;

		const overlay = createTooltip({
			mapRef: mapObject,
			tooltipRef: overlayElRef.current,
			offset: [12, -12],
			positioning: 'bottom-left',
			id: TOOLTIP_ID,
		});

		// Pointermove — show tooltip with formatted feature properties
		const moveKey = mapObject.on('pointermove', e => {
			if (e.dragging || !toolBarVisibleRef.current || interactionsDisabledRef.current) {
				overlay.setPosition(undefined);
				return;
			}

			const feature = getCollectionFeature(mapObject, e.pixel);
			if (!feature) {
				overlay.setPosition(undefined);
				const el = mapObject.getTargetElement();
				if (el) el.style.cursor = '';
				return;
			}

			const el = mapObject.getTargetElement();
			if (el) el.style.cursor = 'pointer';

			const props = feature.getProperties();
			// Read at hover time, so a rename shows without re-binding the handler.
			const objectName = objectLabel(useCollectionStore.getState().objects, props.node_uuid);

			setTooltipContent(
				<Flex vertical gap={2}>
					<Text strong style={{ fontSize: '0.8rem', color: '#fff' }}>
						{objectName}
					</Text>
					{['confidence', 'height', 'area', 'radius', 'estimated_stems'].map(key => {
						const formatted = formatProperty(key, props[key]);
						if (!formatted) return null;
						const lines = Array.isArray(formatted) ? formatted : [formatted];
						return (
							<Fragment key={key}>
								{lines.map((line, i) => (
									<Text
										key={i}
										style={{
											fontSize: '0.75rem',
											color: 'rgba(255, 255, 255, 0.85)',
										}}>
										{line}
									</Text>
								))}
							</Fragment>
						);
					})}
				</Flex>
			);
			overlay.setPosition(e.coordinate);
		});

		// Click — copy coordinates to clipboard
		const clickKey = mapObject.on('click', async e => {
			if (e.dragging || interactionsDisabledRef.current) return;

			const feature = getCollectionFeature(mapObject, e.pixel);
			if (!feature) return;

			const center = feature.get('center');
			if (center?.x == null || center?.y == null) return;

			const coords = toLonLat([center.x, center.y], projectionRef.current).reverse();
			const formatted = coords.map(c => c.toFixed(5)).join(', ');

			try {
				await navigator.clipboard.writeText(formatted);
				addToastRef.current({
					id: 'collection-copy-coords',
					title: 'Coordinates copied',
					message: formatted,
					bg: 'success',
				});
			} catch {
				addToastRef.current({
					id: 'collection-copy-coords-error',
					title: 'Could not copy coordinates',
					message: formatted,
					bg: 'danger',
					autohide: false,
				});
			}
		});

		return () => {
			unByKey(moveKey);
			unByKey(clickKey);
			mapObject.removeOverlay(overlay);
			const el = mapObject.getTargetElement();
			if (el) el.style.cursor = '';
		};
	}, [mapObject, enabled]);

	return { tooltipContent };
};

export default useCollectionTooltip;
